export const Product = {
  Free: 'FREE',
  IdentityPlus: 'Identity Plus',
  CreditExpert: 'CreditExpert',
} as const;

export type Product = (typeof Product)[keyof typeof Product];

export const PlanType = {
  Free: 'FREE',
  Paid: 'PAID',
} as const;

export type PlanType = (typeof PlanType)[keyof typeof PlanType];

export const CancellationType = {
  Immediate: 'IMMEDIATE',
  Deferred: 'DEFERRED',
} as const;

export type CancellationType = (typeof CancellationType)[keyof typeof CancellationType] | null;

const COMMON_COPY = {
  whatHappensNextTitle: 'What happens next?',
  stopFuturePayments: 'We’ll stop future payments.',
  reactivateAnytime: 'You can reactivate your free Experian account anytime.',
  loggedOutImmediately: 'You’ll be logged out of your account.',
};

export const DEACTIVATE_ACCOUNT_TITLE = "You're about to deactivate your account";
export const IMMEDIATE_DEACTIVATE_ACCOUNT_TITLE = 'Deactivate account now';
export const DEFERRED_DEACTIVATE_ACCOUNT_TITLE = (date: string) => `Deactivate account on ${date}`;

export const ID_COOLING_OFF_USER_DEACTIVATE_ACCOUNT_INFO = {
  title: 'It means you’ll lose access to:',
  helperTextList: [
    'Dedicated fraud support',
    'Timeline of your score changes',
    'What’s impacting your credit score',
    'Lock your credit report to keep it safe',
    'Access to your account',
  ],
};

export const CE_COOLING_OFF_USER_DEACTIVATE_ACCOUNT_INFO = {
  title: 'It means you’ll lose access to:',
  helperTextList: [
    'Daily score and report updates',
    'Detailed view of your score timeline',
    'What’s impacting your credit score',
    'Access to your account',
  ],
};

type GetDeactivateAccountInfoParams = {
  product: Product;
  planType: PlanType;
  cancellationType: CancellationType;
  formattedBillingEndDate?: string;
  isCoolingOffEligible?: boolean;
};

export const getDeactivateAccountInfo = ({
  product,
  planType,
  cancellationType,
  formattedBillingEndDate,
  isCoolingOffEligible,
}: GetDeactivateAccountInfoParams) => {
  if (planType === PlanType.Free) {
    return {
      title: COMMON_COPY.whatHappensNextTitle,
      helperTextList: [
        "We'll deactivate your Experian account immediately.",
        'You will be logged out immediately.',
        COMMON_COPY.reactivateAnytime,
      ],
    };
  }

  if (planType === PlanType.Paid && cancellationType === null) {
    if (isCoolingOffEligible && product === Product.IdentityPlus) {
      return ID_COOLING_OFF_USER_DEACTIVATE_ACCOUNT_INFO;
    }

    if (isCoolingOffEligible && product === Product.CreditExpert) {
      return CE_COOLING_OFF_USER_DEACTIVATE_ACCOUNT_INFO;
    }

    return {
      title: COMMON_COPY.whatHappensNextTitle,
      helperTextList: [
        `We’ll stop your ${product} subscription and deactivate your Experian account at the end of your billing period on ${formattedBillingEndDate}.`,
        COMMON_COPY.stopFuturePayments,
        COMMON_COPY.reactivateAnytime,
      ],
    };
  }

  if (planType === PlanType.Paid && cancellationType === CancellationType.Deferred) {
    return {
      title: COMMON_COPY.whatHappensNextTitle,
      helperTextList: [
        `You’ll still have access to your account and ${product} benefits until ${formattedBillingEndDate}.`,
        COMMON_COPY.stopFuturePayments,
        COMMON_COPY.reactivateAnytime,
      ],
    };
  }

  if (cancellationType === CancellationType.Immediate) {
    return {
      title: COMMON_COPY.whatHappensNextTitle,
      helperTextList: [
        `You’ll lose access to your ${product} benefits and account immediately.`,
        COMMON_COPY.loggedOutImmediately,
        COMMON_COPY.stopFuturePayments,
        'You’ll get a refund within 14 days.',
        COMMON_COPY.reactivateAnytime,
      ],
    };
  }

  return null;
};
